import { useMemo } from "react";
import { useTranslation } from 'react-i18next';

// Stable reference for default fields to prevent infinite loops
const DEFAULT_FIELDS = ['name', 'title', 'description'];

/**
 * Transform translatable fields from multilingual objects to current language strings
 * @param {any} data - The data to transform
 * @param {string[]} fields - Array of field names to translate
 * @param {string} currentLang - Current language to use for translation
 * @returns {any} Transformed data with translated fields
 */
const transformTranslatableFields = (data, fields = DEFAULT_FIELDS, currentLang = 'en') => {
    if (!data || typeof data !== 'object') return data;
    
    // Handle arrays
    if (Array.isArray(data)) {
        return data.map(item => transformTranslatableFields(item, fields, currentLang));
    }
    
    // Handle objects
    const transformed = { ...data };
    
    // Helper function to check if an object is multilingual
    const isMultilingualObject = (obj) => {
        if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
        const keys = Object.keys(obj);
        return keys.some(k => 
            k === 'en' || k === 'fr' || k === 'de' || k === 'es' || k === 'it' || 
            (k.length === 2 && k.toLowerCase() === k)
        ) && keys.length <= 5; // Reasonable number of language keys
    };
    
    // Helper function to extract translation from multilingual object
    const getTranslation = (obj) => {
        if (!isMultilingualObject(obj)) return obj;
        return obj[currentLang] || obj['en'] || obj['fr'] || Object.values(obj)[0] || '';
    };
    
    // First pass: Transform ALL objects that look like multilingual objects
    Object.keys(transformed).forEach(key => {
        if (isMultilingualObject(transformed[key])) {
            transformed[key] = getTranslation(transformed[key]);
        }
    });
    
    // Second pass: Recursively transform nested objects and arrays FIRST
    // This ensures nested objects are fully transformed before we try to access their properties
    Object.keys(transformed).forEach(key => {
        if (transformed[key] && typeof transformed[key] === 'object') {
            transformed[key] = transformTranslatableFields(transformed[key], fields, currentLang);
        }
    });
    
    // Third pass: Transform specified fields (handle both simple and nested field paths)
    // This happens AFTER recursive transformation to ensure nested objects are ready
    fields.forEach(field => {
        const fieldParts = field.split('.');
        let current = transformed;
        
        // Navigate to the parent object
        for (let i = 0; i < fieldParts.length - 1; i++) {
            if (current[fieldParts[i]]) {
                current = current[fieldParts[i]];
            } else {
                return; // Path doesn't exist
            }
        }
        
        const finalField = fieldParts[fieldParts.length - 1];
        
        // Transform the final field if it's a multilingual object
        if (isMultilingualObject(current[finalField])) {
            current[finalField] = getTranslation(current[finalField]);
        }
    });
    
    return transformed;
};

/**
 * Keeps translated data in sync with language changes for admin panel.
 * @param {any} rawData - The raw API data (with all language fields).
 * @param {string[]} fields - Optional array of field names to translate specifically.
 * @returns {any} The translated data for the current language.
 */
export function useTranslatableData(rawData, fields = DEFAULT_FIELDS) {
    // Use the i18n object from useTranslation for reliable language detection
    const { i18n } = useTranslation();
    
    // Memoize fields to prevent unnecessary re-renders when array contents are the same
    const memoizedFields = useMemo(() => fields, [fields]);
    
    // Memoize the translation computation to prevent infinite loops
    const translated = useMemo(() => {
        return transformTranslatableFields(rawData, memoizedFields, i18n.language);
    }, [rawData, memoizedFields, i18n.language]);

    return translated;
}
